import React, { useContext, useEffect, useReducer } from 'react';
import { changeModifierPositionReducer } from './changeModifierPositionReducer';
import { ChangeModifierPositionActionTypes } from './changeModifierPositionActions';
import { AppContext, IAppContext } from 'appState/appContext';
import {
	ChangeModifierPositionContext,
	changeModifierPositionInitialState,
	TChangeModifierPositionSelectedItem,
	TTypeOfChangeModifierPositionModalWindow,
} from './changeModifierPositionContext';
import { IGetModifierPositionList, IModifierPositionItem, IUpdateModifierPositions } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { getModifierPositionList } from 'backend/modifiers';

const ChangeModifierPositionState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(changeModifierPositionReducer, changeModifierPositionInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const { response, loading, refetch } = useFetchData<IGetModifierPositionList, string>(
		state.IDSelectedModifierGroup ? getModifierPositionList : null,
		state.IDSelectedModifierGroup || '',
	);

	const setIDCustomer = (value: TChangeModifierPositionSelectedItem) => {
		dispatch({
			type: ChangeModifierPositionActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	};

	const setIDRestaurant = (value: TChangeModifierPositionSelectedItem) => {
		dispatch({
			type: ChangeModifierPositionActionTypes.SET_ID_RESTAURANT,
			payload: {
				IDSelectedRestaurant: value,
			},
		});
	};

	const setIDModifierGroup = (value: TChangeModifierPositionSelectedItem) => {
		dispatch({
			type: ChangeModifierPositionActionTypes.SET_ID_MODIFIER_GROUP,
			payload: {
				IDSelectedModifierGroup: value,
			},
		});
	};

	const resetSelectedControls = () => dispatch({ type: ChangeModifierPositionActionTypes.RESET_SELECTED_CONTROLS });

	const setPositionsData = (value: IModifierPositionItem[]) => {
		dispatch({
			type: ChangeModifierPositionActionTypes.SET_POSITIONS_DATA,
			payload: {
				positionsData: value,
			},
		});
	};

	const reorderItems = (value: IModifierPositionItem[]) => {
		dispatch({
			type: ChangeModifierPositionActionTypes.REORDER_ITEMS,
			payload: {
				newOrder: value,
			},
		});
	};

	const setDataForSubmit = (value: IUpdateModifierPositions | null) => {
		dispatch({
			type: ChangeModifierPositionActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfChangeModifierPositionModalWindow) => {
		dispatch({
			type: ChangeModifierPositionActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	useEffect(() => {
		dispatch({
			type: ChangeModifierPositionActionTypes.SET_POSITIONS_DATA,
			payload: {
				positionsData: response?.data.modifierPositions || [],
			},
		});
	}, [response]);

	useEffect(() => {
		refetch();
	}, [state.IDSelectedModifierGroup]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	return (
		<ChangeModifierPositionContext.Provider
			value={{
				setIDCustomer,
				setIDRestaurant,
				setIDModifierGroup,
				resetSelectedControls,
				setPositionsData,
				reorderItems,
				setDataForSubmit,
				changeTypeOfModalWindow,
				IDSelectedCustomer: state.IDSelectedCustomer,
				IDSelectedRestaurant: state.IDSelectedRestaurant,
				IDSelectedModifierGroup: state.IDSelectedModifierGroup,
				positionsData: state.positionsData,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</ChangeModifierPositionContext.Provider>
	);
};

export default ChangeModifierPositionState;
